import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api/client";
import { Alert } from "../components/Ui";

const BLANK = { key: "", label: "", type: "text", required: true, maxLength: 64, options: [] };

const GAME_PRESETS = {
  idOnly: [
    {
      ...BLANK,
      key: "userId",
      label: "Enter your User ID",
      type: "number",
      required: true,
      maxLength: 32,
      options: [],
      pattern: "\\d{4,20}",
    },
  ],

  idZone: [
    {
      ...BLANK,
      key: "userId",
      label: "Enter your User ID",
      type: "number",
      required: true,
      maxLength: 32,
      options: [],
      pattern: "\\d{4,20}",
    },
    {
      ...BLANK,
      key: "zoneId",
      label: "Enter your Zone ID",
      type: "number",
      required: true,
      maxLength: 16,
      options: [],
      pattern: "\\d{1,10}",
    },
  ],

  genshin: [
    {
      ...BLANK,
      key: "userId",
      label: "Enter your UID",
      type: "number",
      required: true,
      maxLength: 20,
      options: [],
      pattern: "\\d{6,20}",
    },
    {
      ...BLANK,
      key: "zoneId",
      label: "Select Server",
      type: "select",
      required: true,
      maxLength: 20,
      options: [
        "America",
        "Asia",
        "Europe",
        "TW, HK, MO",
      ],
      pattern: "",
    },
  ],
};

export default function AdminProductEdit() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [tab, setTab] = useState("details");

  const reload = () => api.get(`/admin/products/${id}`).then((r) => setProduct(r.data.product)).catch(() => {});

  useEffect(() => { reload(); }, [id]);

  if (!product) return <p className="muted">Loading…</p>;

  return (
    <div>
      <header style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <img src={`/${product.image}`} alt="" style={{ width: 56, height: 56, borderRadius: 12, objectFit: "cover" }}
          onError={(e) => { e.target.style.display = "none"; }} />
        <div>
          <h1 style={{ margin: 0 }}>{product.name}</h1>
          <p className="muted small" style={{ margin: 0 }}>
            {product.api === "yes" ? `${product.apiName} · ${product.region}` : "Manual"} · {product.cost.length} packs
          </p>
        </div>
      </header>

      <nav style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--edge)", marginBottom: 24 }}>
        {[["details", "Details"], ["packs", "Packs & prices"], ["fields", "Input fields"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{
              background: "none", border: 0, padding: "10px 16px", fontSize: 14, fontWeight: 700,
              color: tab === k ? "var(--text)" : "var(--mute)",
              borderBottom: `2px solid ${tab === k ? "var(--brand)" : "transparent"}`, marginBottom: -1, cursor: "pointer",
            }}>
            {label}
          </button>
        ))}
      </nav>

      {tab === "details" && <DetailsEditor product={product} onSaved={reload} />}
      {tab === "packs" && <PackTable productId={id} packs={product.cost} onChanged={reload} apiName={product.apiName} />}
      {tab === "fields" && <FieldEditor productId={id} initial={product.inputFields || []} />}
    </div>
  );
}

function DetailsEditor({ product, onSaved }) {
  const [form, setForm] = useState({
    name: product.name || "", category: product.category || "", company: product.company || "",
    desc: product.desc || "", descTwo: product.descTwo || "",
    api: product.api || "no", apiName: product.apiName || "", region: product.region || "",
    fields: product.fields || "2", tagOne: product.tagOne || "", tagTwo: product.tagTwo || "",
    playerCheckBtn: product.playerCheckBtn || "no", disabled: !!product.disabled,
    stock: product.stock || "yes",
    sortOrder: product.sortOrder ?? 0,
  });
  const [image, setImage] = useState(null);
  const [bannerImage, setBannerImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };

  async function save() {
    setBusy(true); setError(null); setSaved(false);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      if (image) fd.append("image", image);
      if (bannerImage) fd.append("bannerImage", bannerImage);
      await api.patch(`/admin/products/${product._id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSaved(true);
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ maxWidth: 600 }}>
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <div>
          <label className="muted small" style={{ display: "block", marginBottom: 4 }}>Product image</label>
          <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} />
        </div>
        <div>
          <label className="muted small" style={{ display: "block", marginBottom: 4 }}>Banner image</label>
          <input type="file" accept="image/*" onChange={(e) => setBannerImage(e.target.files[0])} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <label><span className="muted small">Name</span>
          <input className="input" value={form.name} onChange={(e) => set("name", e.target.value)} /></label>
        <label><span className="muted small">Company</span>
          <input className="input" value={form.company} onChange={(e) => set("company", e.target.value)} /></label>
        <label><span className="muted small">Category</span>
          <input className="input" value={form.category} onChange={(e) => set("category", e.target.value)} /></label>
        <label><span className="muted small">Display order (lower = first)</span>
          <input className="input tabular" type="number" value={form.sortOrder} onChange={(e) => set("sortOrder", e.target.value)} /></label>
        <label><span className="muted small">Fulfilment</span>
          <select className="input" value={form.api} onChange={(e) => set("api", e.target.value)}>
            <option value="no">Manual</option><option value="yes">Auto (API)</option>
          </select></label>
        {form.api === "yes" && <>
          <label><span className="muted small">API provider</span>
            <select className="input" value={form.apiName} onChange={(e) => set("apiName", e.target.value)}>
              <option value="">Select…</option><option value="smileOne">SmileOne</option><option value="yokcash">Yokcash</option><option value="bgmi">BGMI (Busan API)</option><option value="busan">Busan (1GameStopUp API)</option>
            </select></label>
          <label><span className="muted small">Region</span>
            <select className="input" value={form.region} onChange={(e) => set("region", e.target.value)}>
              <option value="">Select…</option><option value="brazil">Brazil</option><option value="philippines">Philippines</option>
            </select></label>
        </>}
        <label><span className="muted small">Player check</span>
          <select className="input" value={form.playerCheckBtn} onChange={(e) => set("playerCheckBtn", e.target.value)}>
            <option value="no">Disabled</option><option value="yes">Enabled</option>
          </select></label>
        <label style={{ display: "flex", alignItems: "flex-end", gap: 8, paddingBottom: 10 }}>
          <input type="checkbox" checked={!!form.disabled} onChange={(e) => set("disabled", e.target.checked)} />
          <span className="small">Disabled (hidden from store)</span>
        </label>
        <label style={{ display: "flex", alignItems: "flex-end", gap: 8, paddingBottom: 10 }}>
          <input type="checkbox" checked={form.stock !== "no"} onChange={(e) => set("stock", e.target.checked ? "yes" : "no")} />
          <span className="small">In Stock</span>
        </label>
      </div>

      <label style={{ display: "block", marginBottom: 12 }}><span className="muted small">Description</span>
        <textarea className="input" rows={3} value={form.desc} onChange={(e) => set("desc", e.target.value)} style={{ width: "100%", resize: "vertical" }} /></label>

      <Alert>{error}</Alert>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button className="btn primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save details"}</button>
        {saved && <span className="small" style={{ color: "var(--ok)" }}>Saved</span>}
      </div>
    </div>
  );
}

function PackTable({ productId, packs, onChanged, apiName }) {
  const [rows, setRows] = useState(packs);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [newPack, setNewPack] = useState(null);
  const [editPack, setEditPack] = useState(null);

  const isBusan = /^busan$/i.test(apiName || "");
  const [catalog, setCatalog] = useState([]);
  const [catErr, setCatErr] = useState(null);
  const [catLoading, setCatLoading] = useState(false);
  const [pickQ, setPickQ] = useState("");

  useEffect(() => {
    if (!isBusan) return;
    let active = true;
    setCatLoading(true);
    api
      .get("/admin/busan/products")
      .then((res) => { if (active) setCatalog(res.data.products || []); })
      .catch((e) => { if (active) setCatErr(e.response?.data?.message || e.message); })
      .finally(() => { if (active) setCatLoading(false); });
    return () => { active = false; };
  }, [isBusan]);

  const parsePrice = (s) => {
    const n = Number(String(s).replace(/[^\d.]/g, ""));
    return Number.isFinite(n) ? n : "";
  };
  const denom = (nm) => {
    const m = String(nm || "").match(/\(([^)]*)\)\s*$/);
    return m ? m[1].trim() : String(nm || "").trim();
  };

  const patch = (packId, k, v) => setRows((r) => r.map((p) => (p.packId === packId ? { ...p, [k]: v } : p)));

  async function save(pack) {
    setSaving(pack.packId); setError(null);
    try {
      await api.patch(`/admin/products/${productId}/packs/${pack.packId}`, {
        price: Number(pack.price), resPrice: pack.resPrice ? Number(pack.resPrice) : undefined, disabled: !!pack.disabled,
        sortOrder: Number(pack.sortOrder) || 0,
      });
      onChanged();
    } catch (e) { setError(e.message); }
    finally { setSaving(null); }
  }

  async function saveEdit() {
    if (!editPack) return;
    setSaving(editPack.packId); setError(null);
    try {
      const fd = new FormData();
      fd.append("amount", editPack.amount || "");
      fd.append("pack", editPack.pack || "");
      fd.append("id", editPack.id || "");
      fd.append("price", editPack.price || "");
      fd.append("resPrice", editPack.resPrice || "");
      fd.append("prodCategory", editPack.prodCategory || "");
      if (editPack.newImage) fd.append("pimg", editPack.newImage);

      await api.patch(`/admin/products/${productId}/packs/${editPack.packId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditPack(null);
      onChanged();
    } catch (e) { setError(e.message); }
    finally { setSaving(null); }
  }

  async function deletePack(packId) {
    if (!window.confirm("Delete this pack permanently?")) return;
    try { await api.delete(`/admin/products/${productId}/packs/${packId}`); onChanged(); }
    catch (e) { setError(e.message); }
  }

  async function addPack() {
    if (!newPack || !newPack.amount || !newPack.price) return setError("Amount and price are required.");
    try {
      const fd = new FormData();
      Object.entries(newPack).forEach(([k, v]) => { if (k !== "newImage") fd.append(k, v); });
      if (newPack.newImage) fd.append("pimg", newPack.newImage);
      // Use JSON for now since add endpoint doesn't have multer
      await api.post(`/admin/products/${productId}/packs`, {
        id: newPack.id || "",
        amount: newPack.amount,
        pack: newPack.pack || "",
        price: newPack.price,
        resPrice: newPack.resPrice || "",
        pimg: "",
        prodCategory: newPack.prodCategory || "",
      });
      setNewPack(null); setPickQ(""); onChanged();
    } catch (e) { setError(e.message); }
  }

  return (
    <div>
      <Alert>{error}</Alert>
      <div className="scroll">
        <table className="table">
          <thead>
            <tr>{["Image", "Pack", "Provider ID", "Retail ₹", "Reseller ₹", "Order", "Live", "", "", ""].map((h, i) => <th key={i}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.packId}>
                <td>
                  {p.pimg ? (
                    <img src={p.pimg.startsWith("http") ? p.pimg : `/${p.pimg}`} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover" }}
                      onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: "var(--ink-600)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🎮</div>
                  )}
                </td>
                <td>
                  <div style={{ fontWeight: 700 }}>{p.amount}</div>
                  <div className="muted small">{p.pack}</div>
                  {p.prodCategory && <div className="muted small" style={{ fontSize: 10 }}>{p.prodCategory}</div>}
                </td>
                <td className="mono" style={{ fontSize: 12 }}>{p.id}
                  {String(p.id).includes("&") && <span className="badge" style={{ marginLeft: 6, background: "rgba(251,191,36,.15)", color: "var(--warn)", fontSize: 10 }}>{String(p.id).split("&").length}x</span>}
                </td>
                <td><input className="input tabular" style={{ width: 80 }} value={p.price} onChange={(e) => patch(p.packId, "price", e.target.value)} /></td>
                <td><input className="input tabular" style={{ width: 80 }} value={p.resPrice || ""} onChange={(e) => patch(p.packId, "resPrice", e.target.value)} /></td>
                <td><input className="input tabular" style={{ width: 60 }} type="number" value={p.sortOrder ?? 0} onChange={(e) => patch(p.packId, "sortOrder", e.target.value)} title="Lower shows first" /></td>
                <td><input type="checkbox" checked={!p.disabled} onChange={(e) => patch(p.packId, "disabled", !e.target.checked)} /></td>
                <td><button className="btn ghost" style={{ padding: "4px 8px", fontSize: 11 }} disabled={saving === p.packId} onClick={() => save(p)}>{saving === p.packId ? "…" : "Save"}</button></td>
                <td><button className="btn ghost" style={{ padding: "4px 8px", fontSize: 11 }} onClick={() => setEditPack({ ...p, newImage: null })}>Edit</button></td>
                <td><button className="btn ghost" style={{ padding: "4px 8px", fontSize: 11, color: "var(--bad)" }} onClick={() => deletePack(p.packId)}>Del</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Pack Modal */}
      {editPack && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}
          onClick={(e) => e.target === e.currentTarget && setEditPack(null)}>
          <div className="card" style={{ padding: 24, maxWidth: 480, width: "90%" }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 16 }}>Edit Pack — {editPack.amount}</h3>

            <div style={{ display: "flex", gap: 16, marginBottom: 16, alignItems: "flex-start" }}>
              <div>
                {(editPack.newImage || editPack.pimg) && (
                  <img src={editPack.newImage ? URL.createObjectURL(editPack.newImage) : (editPack.pimg.startsWith("http") ? editPack.pimg : `/${editPack.pimg}`)}
                    alt="" style={{ width: 64, height: 64, borderRadius: 12, objectFit: "cover", marginBottom: 8, display: "block" }} />
                )}
                <label className="muted small" style={{ display: "block", marginBottom: 4 }}>Pack Image</label>
                <input type="file" accept="image/*" onChange={(e) => setEditPack({ ...editPack, newImage: e.target.files[0] })} style={{ fontSize: 12 }} />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <label><span className="muted small">Pack Name / Amount *</span>
                <input className="input" value={editPack.amount} onChange={(e) => setEditPack({ ...editPack, amount: e.target.value })} /></label>
              <label><span className="muted small">Pack Description</span>
                <input className="input" value={editPack.pack || ""} onChange={(e) => setEditPack({ ...editPack, pack: e.target.value })} placeholder="e.g. 78 + 8" /></label>
              <label><span className="muted small">Provider ID</span>
                <input className="input mono" value={editPack.id || ""} onChange={(e) => setEditPack({ ...editPack, id: e.target.value })} placeholder="e.g. 13 or 13&25" /></label>
              <label><span className="muted small">Category</span>
                <input className="input" value={editPack.prodCategory || ""} onChange={(e) => setEditPack({ ...editPack, prodCategory: e.target.value })} placeholder="e.g. Mobile Legends Diamonds" /></label>
              <label><span className="muted small">Retail Price ₹</span>
                <input className="input tabular" type="number" value={editPack.price} onChange={(e) => setEditPack({ ...editPack, price: e.target.value })} /></label>
              <label><span className="muted small">Reseller Price ₹</span>
                <input className="input tabular" type="number" value={editPack.resPrice || ""} onChange={(e) => setEditPack({ ...editPack, resPrice: e.target.value })} /></label>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn primary" onClick={saveEdit} disabled={saving === editPack.packId}>
                {saving === editPack.packId ? "Saving…" : "Save Changes"}
              </button>
              <button className="btn ghost" onClick={() => setEditPack(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Pack */}
      {newPack ? (
        <div className="card" style={{ padding: 16, marginTop: 16 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 14 }}>New pack</h3>

          {isBusan && (
            <div style={{ marginBottom: 12 }}>
              <span className="muted small">Choose Busan product (auto-fills ID & name)</span>
              <input
                className="input"
                value={pickQ}
                placeholder={catLoading ? "Loading Busan catalog…" : "Search game name or product ID…"}
                onChange={(e) => { setPickQ(e.target.value); if (newPack.id) setNewPack({ ...newPack, id: "" }); }}
              />
              {catErr && (
                <div className="small" style={{ color: "var(--bad)", marginTop: 4 }}>
                  Catalog error: {catErr} — you can still enter the Provider ID manually below.
                </div>
              )}
              {pickQ && !newPack.id && (
                <div className="card" style={{ maxHeight: 220, overflowY: "auto", marginTop: 6, padding: 4 }}>
                  {catalog
                    .filter((p) => {
                      const q = pickQ.toLowerCase();
                      return (p.productName || "").toLowerCase().includes(q) || (p.productId || "").toLowerCase().includes(q);
                    })
                    .slice(0, 40)
                    .map((p) => (
                      <button
                        key={p.productId}
                        type="button"
                        className="btn ghost"
                        style={{ display: "block", width: "100%", textAlign: "left", fontSize: 12, padding: "6px 8px" }}
                        onClick={() => {
                          setNewPack({ ...newPack, id: p.productId, amount: denom(p.productName), resPrice: parsePrice(p.price) });
                          setPickQ(p.productName);
                        }}
                      >
                        <strong>{p.productName}</strong> <span className="muted">· {p.productId} · {p.price}</span>
                      </button>
                    ))}
                  {catalog.length > 0 &&
                    catalog.filter((p) => {
                      const q = pickQ.toLowerCase();
                      return (p.productName || "").toLowerCase().includes(q) || (p.productId || "").toLowerCase().includes(q);
                    }).length === 0 && (
                      <div className="small muted" style={{ padding: 8 }}>No match in Busan catalog.</div>
                    )}
                </div>
              )}
              {newPack.id && (
                <div className="small" style={{ marginTop: 6 }}>
                  ✓ Selected <strong>{newPack.amount}</strong> · <span className="tabular">{newPack.id}</span>{" "}
                  <button
                    type="button"
                    className="btn ghost"
                    style={{ fontSize: 11 }}
                    onClick={() => { setNewPack({ ...newPack, id: "", amount: "", resPrice: "" }); setPickQ(""); }}
                  >
                    change
                  </button>
                </div>
              )}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <label><span className="muted small">Provider ID</span><input className="input" value={newPack.id || ""} onChange={(e) => setNewPack({ ...newPack, id: e.target.value })} /></label>
            <label><span className="muted small">Amount/Name *</span><input className="input" value={newPack.amount || ""} onChange={(e) => setNewPack({ ...newPack, amount: e.target.value })} /></label>
            <label><span className="muted small">Description</span><input className="input" value={newPack.pack || ""} onChange={(e) => setNewPack({ ...newPack, pack: e.target.value })} placeholder="e.g. 78 + 8" /></label>
            <label><span className="muted small">Retail ₹ *</span><input className="input tabular" type="number" value={newPack.price || ""} onChange={(e) => setNewPack({ ...newPack, price: e.target.value })} /></label>
            <label><span className="muted small">Reseller ₹</span><input className="input tabular" type="number" value={newPack.resPrice || ""} onChange={(e) => setNewPack({ ...newPack, resPrice: e.target.value })} /></label>
            <label><span className="muted small">Category</span><input className="input" value={newPack.prodCategory || ""} onChange={(e) => setNewPack({ ...newPack, prodCategory: e.target.value })} /></label>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="btn primary" style={{ fontSize: 12 }} onClick={addPack}>Add pack</button>
            <button className="btn ghost" style={{ fontSize: 12 }} onClick={() => { setNewPack(null); setPickQ(""); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn ghost" style={{ marginTop: 16 }} onClick={() => setNewPack({ id: "", amount: "", pack: "", price: "", resPrice: "", prodCategory: "" })}>+ Add pack</button>
      )}
    </div>
  );
}

function FieldEditor({ productId, initial }) {
  const clonePreset = (name) =>
    GAME_PRESETS[name].map((field) => ({
      ...field,
      options: [...(field.options || [])],
    }));

  const detectGameType = (list) => {
    if (
      list.length === 2 &&
      list[0]?.key === "userId" &&
      list[1]?.key === "zoneId" &&
      list[1]?.type === "select"
    ) {
      return "genshin";
    }

    if (
      list.length === 2 &&
      list[0]?.key === "userId" &&
      list[1]?.key === "zoneId"
    ) {
      return "idZone";
    }

    if (
      list.length === 1 &&
      list[0]?.key === "userId"
    ) {
      return "idOnly";
    }

    return "custom";
  };

  const startingFields = initial.length
    ? initial
    : clonePreset("idZone");

  const [fields, setFields] = useState(startingFields);

  const [gameType, setGameType] = useState(() =>
    detectGameType(startingFields)
  );

  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const patch = (index, key, value) => {
    setGameType("custom");
    setSaved(false);

    setFields((current) =>
      current.map((field, fieldIndex) =>
        fieldIndex === index
          ? {
              ...field,
              [key]: value,
            }
          : field
      )
    );
  };

  const add = () => {
    if (fields.length >= 4) return;

    setGameType("custom");
    setSaved(false);

    setFields((current) => [
      ...current,
      {
        ...BLANK,
      },
    ]);
  };

  const remove = (index) => {
    if (fields.length <= 1) return;

    setGameType("custom");
    setSaved(false);

    setFields((current) =>
      current.filter(
        (_, fieldIndex) => fieldIndex !== index
      )
    );
  };

  const applyGameType = (value) => {
    setGameType(value);
    setSaved(false);
    setError(null);

    if (value !== "custom") {
      setFields(clonePreset(value));
    }
  };

  async function save() {
    setError(null);
    setSaved(false);

    try {
      await api.patch(
        `/admin/products/${productId}/fields`,
        {
          inputFields: fields.map((field) => ({
            key: String(field.key || "").trim(),

            label: String(
              field.label || ""
            ).trim(),

            type: field.type,

            required: !!field.required,

            maxLength:
              Number(field.maxLength) || 64,

            options:
              field.type === "select"
                ? (field.options || [])
                    .map((option) =>
                      String(option).trim()
                    )
                    .filter(Boolean)
                : undefined,

            pattern: field.pattern
              ? String(field.pattern).trim()
              : undefined,
          })),
        }
      );

      setSaved(true);
    } catch (e) {
      setError(e.message);
    }
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <div
        className="card"
        style={{
          padding: 16,
          marginBottom: 18,
        }}
      >
        <label style={{ display: "block" }}>
          <span className="muted small">
            Game type
          </span>

          <select
            className="input"
            value={gameType}
            onChange={(e) =>
              applyGameType(e.target.value)
            }
            style={{ marginTop: 4 }}
          >
            <option value="custom">
              Custom fields
            </option>

            <option value="idOnly">
              ID only — Free Fire / PUBG type
            </option>

            <option value="idZone">
              User ID + Zone ID — MLBB type
            </option>

            <option value="genshin">
              UID + Server — Genshin type
            </option>
          </select>
        </label>

        <p
          className="muted small"
          style={{ margin: "10px 0 0" }}
        >
          Selecting Genshin creates a UID input
          and server dropdown. Click Save fields
          after selecting the game type.
        </p>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 16,
        }}
      >
        <h2
          style={{
            fontSize: 18,
            margin: 0,
          }}
        >
          Input fields
        </h2>

        <span className="muted small">
          {fields.length} of 4
        </span>
      </div>

      {fields.map((field, index) => (
        <div
          key={index}
          className="card"
          style={{
            padding: 16,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            <label>
              <span className="muted small">
                Field key
              </span>

              <input
                className="input mono"
                value={field.key}
                placeholder="userId"
                onChange={(e) =>
                  patch(
                    index,
                    "key",
                    e.target.value
                  )
                }
              />
            </label>

            <label>
              <span className="muted small">
                Label
              </span>

              <input
                className="input"
                value={field.label}
                placeholder="Enter your User ID"
                onChange={(e) =>
                  patch(
                    index,
                    "label",
                    e.target.value
                  )
                }
              />
            </label>

            <label>
              <span className="muted small">
                Type
              </span>

              <select
                className="input"
                value={field.type}
                onChange={(e) =>
                  patch(
                    index,
                    "type",
                    e.target.value
                  )
                }
              >
                <option value="text">
                  Text
                </option>

                <option value="number">
                  Number
                </option>

                <option value="select">
                  Dropdown
                </option>
              </select>
            </label>

            <label
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: 8,
                paddingBottom: 10,
              }}
            >
              <input
                type="checkbox"
                checked={!!field.required}
                onChange={(e) =>
                  patch(
                    index,
                    "required",
                    e.target.checked
                  )
                }
              />

              <span className="small">
                Required
              </span>
            </label>
          </div>

          {field.type === "select" && (
            <label
              style={{
                display: "block",
                marginTop: 12,
              }}
            >
              <span className="muted small">
                Options — one per line
              </span>

              <textarea
                className="input"
                rows={4}
                value={(field.options || []).join(
                  "\n"
                )}
                onChange={(e) =>
                  patch(
                    index,
                    "options",
                    e.target.value.split("\n")
                  )
                }
              />
            </label>
          )}

          {fields.length > 1 && (
            <button
              type="button"
              onClick={() => remove(index)}
              style={{
                background: "none",
                border: 0,
                color: "var(--bad)",
                fontSize: 13,
                marginTop: 12,
                padding: 0,
              }}
            >
              Remove field
            </button>
          )}
        </div>
      ))}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          type="button"
          className="btn ghost"
          onClick={add}
          disabled={fields.length >= 4}
        >
          Add field
        </button>

        <button
          type="button"
          className="btn primary"
          onClick={save}
        >
          Save fields
        </button>

        {saved && (
          <span
            className="small"
            style={{ color: "var(--ok)" }}
          >
            Saved
          </span>
        )}
      </div>

      <Alert>{error}</Alert>
    </div>
  );
}